import { format, subYears } from 'date-fns';
import DatePicker from 'react-datepicker';
import {
  forwardRef, useEffect, useRef, useState,
} from 'react';
import { FormBirthdayPickerInterface, FormElementRefImperative } from '@type/form-types';
import { useSingleFormElement } from '@hooks/useBjForm';
import { FormError } from '@components/common/Forms';
import { IconCalendar } from '@components/common/Forms/DatePickerInput/IconsDatePickerInput';

const maxDate = subYears(new Date(), 15);
const minDate = subYears(new Date(), 80);

/**
 * @description Date picker for choosing the birthday
 * * The use will choose first the year, then the month and then the day
 *
 * @example
 * const [date, setDate] = useState<Date|null>(null)
 * ...
 * <BirthdayInput date={date} setDate={setDate}/>
 *
 * @param date
 * @param setDate
 */
export const BirthdayInput = forwardRef<FormElementRefImperative, FormBirthdayPickerInterface>((props, ref) => {
  // component props
  const {
    // picker props
    date,
    setDate,

    // input props
    className = '',
    required,
    withError = true,
    placeholder = '',
    patternMismatchMessage = '',
    customValidityMessages,
    ...attributes
  } = props;

  // form elements shared logic hook
  const {
    setElementValue, checkElementValidity, elementRef, isInvalid, errorMSG, errorClass,
  } = useSingleFormElement({
    ref,
    patternMismatchMessage,
    customValidityMessages,
    className,
  });

  const yearPickerRef = useRef<DatePicker>({} as DatePicker);
  const monthPickerRef = useRef<DatePicker>({} as DatePicker);
  const dayPickerRef = useRef<DatePicker>({} as DatePicker);

  // The pickers anchor to zero-width spans, so on narrow screens the floating calendar can land
  // off-screen. On mobile render it in a centered portal overlay instead; keep the inline popper
  // on desktop.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const dateFormat = 'dd.MM.yyyy';

  // set the input value when the date changes
  useEffect(() => {
    setElementValue(date ? format(date, dateFormat) : '');
  }, [date, setElementValue]);

  // Handle year selection - opens month picker
  // Uses onSelect instead of onChange to handle both new selections and re-selections of the same year
  const onSelectYear = (newDate: Date | null) => {
    if (newDate) {
      setDate(newDate);
    }
    // Always open month picker, even if same year was clicked
    monthPickerRef.current.setOpen(true);
  };

  // Handle month selection - opens day picker
  // Uses onSelect instead of onChange to handle both new selections and re-selections of the same month
  const onSelectMonth = (newDate: Date | null) => {
    if (newDate) {
      setDate(newDate);
    }
    // Always open day picker, even if same month was clicked
    dayPickerRef.current?.setOpen(true);
  };

  // Handle day selection - closes day picker
  // Uses onSelect instead of onChange to handle both new selections and re-selections of the same day
  const onSelectDay = (newDate: Date | null) => {
    if (newDate) {
      setDate(newDate);
    }
    dayPickerRef.current?.setOpen(false);
  };

  const onOpenPicker = () => {
    yearPickerRef.current?.setOpen(true);
  };

  return (
    <div>
      <div className="relative" onClick={onOpenPicker} role="presentation">
        {/* calendar icon */}
        <div className="absolute flex size-full justify-end">
          <div className="flex h-full flex-col justify-center pr-4">
            <IconCalendar />
          </div>
        </div>
        <input
          className={`pl-3 pr-10 ${className}${isInvalid ? errorClass : ''}`}
          ref={elementRef}
          placeholder={placeholder}
          required={required}
          type="text"
          {...attributes}
        />
      </div>
      { // input's error field - show only if input is required and withError is true (default true)
        required && withError && <FormError errorMessage={errorMSG} isShowing={isInvalid} />
      }

      <div className="relative w-full [&>div]:flex">
        <DatePicker
          showYearPicker
          dateFormat="yyyy"
          selected={date}
          minDate={minDate}
          maxDate={maxDate}
          showPopperArrow={false}
          withPortal={isMobile}
          onSelect={onSelectYear}
          customInput={<span />}
          ref={yearPickerRef}
          onClickOutside={() => {
            checkElementValidity();
          }}
        />

        <DatePicker
          showMonthYearPicker
          showPopperArrow={false}
          withPortal={isMobile}
          dateFormat="yyyy/MM"
          selected={date}
          onSelect={onSelectMonth}
          customInput={<span />}
          ref={monthPickerRef}
        />

        <DatePicker
          dateFormat="yyyy/MM/dd"
          showPopperArrow={false}
          withPortal={isMobile}
          selected={date}
          onSelect={onSelectDay}
          customInput={<span />}
          ref={dayPickerRef}
        />
      </div>
    </div>
  );
});
